<template>
  <layout>
    <template slot="navbar">
      <navbar class="has-shadow">
        <search-field
          placeholder="Кадастровый номер земельного участка..."
          input-class="is-medium"
          :url="searchUrl"
          v-model="selectedZu"
        ></search-field>
      </navbar>
    </template>
    <div>
      <div class="columns navbar-dropdown-fix">
        <div class="column">
          <leaflet-map-loader
            :mapConfig="mapConfig"
            v-if="mapConfig"
            mapClass="map"
          >
            <template slot-scope="{ leaflet, map }">
              <leaflet-background-layer
                v-if="activeBackgroundLayer"
                :leaflet="leaflet"
                :map="map"
                :background-layer="activeBackgroundLayer"
              ></leaflet-background-layer>
              <leaflet-geometry-mask
                v-if="geometryJson"
                :map="map"
                :leaflet="leaflet"
                :geometry-json="geometryJson"
                :mask-config="maskConfig"
              ></leaflet-geometry-mask>

              <leaflet-map-raster-layer
                v-for="layer in visibleLayers"
                :key="layer.name"
                :layer="layer"
                :leaflet="leaflet"
                :map="map"
                :subdomains="subdomains"
              />
              <leaflet-map-feature-under-cursor-search
                :map="map"
                :mask-geometry-json="geometryJson"
              ></leaflet-map-feature-under-cursor-search>

              <leaflet-map-fit-bound
                :map="map"
                :leaflet="leaflet"
                :geoJsonGeometry="selectedZu.geoJson"
                v-if="selectedZu"
              ></leaflet-map-fit-bound>

              <leaflet-map-search-marker
                :map="map"
                :leaflet="leaflet"
                :location="searchLocation"
                :loading="mapSearchLoading"
              ></leaflet-map-search-marker>

              <leaflet-map-selection
                :map="map"
                :leaflet="leaflet"
                :geoJsonGeometry="highlightedGeometry"
              ></leaflet-map-selection>
              <portal to="map-buttons-top-right">
                <leaflet-map-tools></leaflet-map-tools>
                <leaflet-map-print-button></leaflet-map-print-button>
              </portal>
              <portal to="map-buttons-bottom-right">
                <leaflet-map-zoom-buttons :map="map"></leaflet-map-zoom-buttons>
              </portal>
            </template>
          </leaflet-map-loader>
        </div>
        <leaflet-map-buttons-panel
          position-class="top right"
          name="map-buttons-top-right"
        ></leaflet-map-buttons-panel>
        <leaflet-map-buttons-panel
          position-class="bottom right"
          name="map-buttons-bottom-right"
        ></leaflet-map-buttons-panel>
      </div>
      <first-run-hint></first-run-hint>
      <left-panel :is-active="hasSearchResult" @close="reset">
        <search-result :search-result="searchResult"></search-result>
      </left-panel>

      <transition name="fade">
        <left-panel
          :is-active="hasActiveDetailObject"
          @close="resetActiveDetailObject"
          :class="activeDetailObjectPanelClasses"
          v-if="hasActiveDetailObject"
        >
          <component
            :is="activeDetailComponent.type"
            :project-schema="currentProject.schema"
            v-bind="activeDetailComponent.data"
          ></component>
        </left-panel>
      </transition>
    </div>
  </layout>
</template>

<script>
// @ is an alias to /src
import Layout from "./Layout";
import Navbar from "@/components/Navbar.vue";
import SearchField from "@/components/map/SearchField";
import LeafletMapLoader from "@/components/map/leaflet/LeafletMapLoader";
import LeafletMapRasterLayer from "@/components/map/leaflet/LeafletMapRasterLayer";
import LeafletMapFeatureUnderCursorSearch from "@/components/map/leaflet/LeafletMapFeatureUnderCursorSearch";
import LeafletMapSelection from "@/components/map/leaflet/LeafletMapSelection";
import LeafletMapFitBound from "@/components/map/leaflet/LeafletMapFitBound";
import LeafletMapPrintButton from "@/components/map/leaflet/LeafletMapPrintButton";
import LeafletMapButtonsPanel from "@/components/map/leaflet/LeafletMapButtonsPanel";
import LeafletMapZoomButtons from "../components/map/leaflet/LeafletMapZoomButtons";
import LeafletMapSearchMarker from "@/components/map/leaflet/LeafletMapSearchMarker";
import LeafletGeometryMask from "../components/map/leaflet/LeafletGeometryMask";
import LeafletBackgroundLayer from "../components/map/leaflet/LeafletBackgroundLayer";
import LeafletMapTools from "../components/map/leaflet/LeafletMapTools";
import LeftPanel from "@/components/map/LeftPanel";
import SearchResult from "@/components/map/SearchResult";
import GeoFeatureProperties from "@/components/map/GeoFeatureProperties";
import Group from "@/components/map/documents/Group";
import GisogdDocProperties from "@/components/map/GisogdDocProperties";
import FirstRunHint from "@/components/map/FirstRunHint";

import store from "../store";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  props: {
    projectSchema: { type: String },
  },
  components: {
    Layout,
    Navbar,
    SearchField,
    LeafletMapSearchMarker,
    LeafletMapSelection,
    LeafletMapFitBound,
    LeafletMapLoader,
    LeafletMapRasterLayer,
    LeafletMapFeatureUnderCursorSearch,
    LeafletMapButtonsPanel,
    LeafletMapZoomButtons,
    LeafletMapPrintButton,
    LeafletMapTools,
    LeafletGeometryMask,
    LeafletBackgroundLayer,
    LeftPanel,
    SearchResult,
    GeoFeatureProperties,
    Group,
    GisogdDocProperties,
    FirstRunHint,
  },
  data() {
    return {
      selectedZu: null,
    };
  },
  computed: {
    ...mapState([
      "mapConfig",
      "searchResult",
      "currentProject",
      "activeDetailComponent",
      "searchLocation",
      "highlightedGeometry",
      "mapSearchLoading",
      "activeBackgroundLayer",
    ]),
    ...mapGetters(["visibleLayers"]),
    hasSearchResult() {
      return this.searchResult !== null;
    },
    hasActiveDetailObject() {
      return this.activeDetailComponent != null;
    },
    activeDetailObjectPanelClasses() {
      if (this.hasActiveDetailObject) return "is-2";
      return "";
    },
    geometryJson() {
      return this.mapConfig.boundsGeometryJson
        ? JSON.parse(this.mapConfig.boundsGeometryJson)
        : null;
    },
    subdomains() {
      return this.mapConfig.leafletSubDomains &&
        this.mapConfig.leafletSubDomains.subDomains
        ? this.mapConfig.leafletSubDomains.subDomains
        : null;
    },
    maskConfig() {
      return this.mapConfig.leafletMaskConfig;
    },
    searchUrl() {
      return this.currentProject
        ? `v1/zusearch/${this.currentProject.schema}`
        : "";
    },
  },
  methods: {
    ...mapActions([
      "reset",
      "resetActiveDetailObject",
      "setHighlightedGeometry",
    ]),
  },
  watch: {
    selectedZu() {
      this.setHighlightedGeometry(null);
    },
  },
  async beforeRouteEnter(to, from, next) {
    await beforeRouter(to, from, next);
  },
  async beforeRouteUpdate(to, from, next) {
    this.setHighlightedGeometry(null);
    await beforeRouter(to, from, next);
  },
  beforeRouteLeave(to, from, next) {
    this.setHighlightedGeometry(null);
    next();
  },
};

const beforeRouter = async (to, from, next) => {
  await store.dispatch("fetchMapConfigAsync", {
    gisogdSection: to.query.section,
    infoSetKey: to.query.infoSetKey,
    docKey: to.query.docKey,
    onlyDocument: to.query.onlyDocument,
  });
  next();
};
</script>
<style lang="scss">
@import "../assets/sass/main.scss";

.map {
  height: calc(100vh - #{$navbar-height});
  width: 100%;
}

.navbar-dropdown-fix {
  position: relative;
  z-index: 0;
}

@media print {
  .map {
    position: absolute;
    width: 100vw;
    height: 100vh;
  }
}
</style>
